import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { UnsubscribeOnDestroyAdapter } from "../../adapters/unsubscibe-on-destroy.adapter";
import { inOutAnimation } from "../../animations";
import { AnalysisService } from "../../../services/analysis.service";
import { LoaderService } from "../../../services/loader.service";
import { SolarCalculatorRequest } from "../../models/analysis/solar-calculator-request";
import { ProductionTypeFactory } from './common/production-types/production-type-factory';
import { ConsumptionTypeFactory } from './common/consumption-types/consumption-type-factory';
import { ToastService } from '../../../services/toast.service';
import { CalculatorResultWrapper } from '../../models/analysis/calculator-result-wrapper';
import { fileTypeValidator } from '../../validators';
import { CalculatorInputManager } from './common/calculator-input-manager';
import { AuthService } from '../../../services/auth/auth.service';
import { UserRole } from '../../models/user/user-role.enum';
import { Observable } from 'rxjs';
import { RoofSegmentSizeAndSunshineStats } from '../../models/maps/building-insights';

@Component({
  selector: 'psc-calculator',
  templateUrl: './calculator.component.html',
  animations: [
    inOutAnimation({ enterDelay: 0.5 }),
    inOutAnimation({ name: 'inOutFromRight', translationDirection: 'X' }),
  ],
})
export class CalculatorComponent extends UnsubscribeOnDestroyAdapter implements OnInit {

  @Input() advanced: boolean = false;
  @Input() source: string | null = null;

  @Output() resultCalculated: EventEmitter<CalculatorResultWrapper> = new EventEmitter<CalculatorResultWrapper>();

  formGroup: FormGroup;

  manager: CalculatorInputManager = new CalculatorInputManager();

  protected readonly UserRole = UserRole;
  protected readonly SiteDataInputType = SiteDataInputType;

  constructor(
    private formBuilder: FormBuilder,
    private analysisService: AnalysisService,
    private loadingService: LoaderService,
    private toastService: ToastService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      country: ['', Validators.required],
      supplier: ['', Validators.required],
      tariff_group: ['', Validators.required],
      type_calculation: ['', Validators.required],
      consumption: this.formBuilder.array([]),
      consumption_input_type: ['', Validators.required],
      consumption_unit_type: !this.advanced,
      consumption_file: ['', [fileTypeValidator(['csv', 'xlsx', 'xls'])]],
      site_data_file: ['', [fileTypeValidator(['csv'])]],
      user_contact: this.authService.getUserEmail() || '',
    });
  }

  setAreaValue(projectedArea: number) {
    this.manager.drawingModeActive = false;
    const trueAreaValue = this.calculateRoofSurfaceArea(projectedArea, this.formGroup.controls['tilt'].value);
    this.formGroup.controls['area'].setValue(trueAreaValue.toFixed());
  }

  calculateRoofSurfaceArea(projectedArea: number, tiltAngleDegrees: number): number {
    const tiltAngleRadians = tiltAngleDegrees * Math.PI / 180;
    return projectedArea / Math.cos(tiltAngleRadians);
  }

  setLocation(location: google.maps.LatLngLiteral) {
    this.formGroup.controls['location'].setValue(location);
    this.manager.location.next(location);
  }

  onSubmit() {
    const formValue = this.formGroup.value;

    const request: SolarCalculatorRequest = {
      priceID: formValue.tariff_group,
      siteDataInputType: formValue.site_data_type || SiteDataInputType.PVGIS,
      typeCalculation: formValue.type_calculation,
      userContact: formValue.user_contact,
      source: this.source || undefined,
    };


    if (this.manager.consumptionFileData &&
      this.manager.productionData &&
      this.manager.consumptionFileData?.length !== this.manager.productionData?.length) {
      /**
       * t(home.calculator.files_not_matching)
       */
      this.toastService.showErrorToast('home.calculator.files_not_matching');
      return;
    }

    const productionTypeObject = ProductionTypeFactory.getProductionTypeInstance(formValue['site_data_type']);
    const consumptionTypeObject = ConsumptionTypeFactory.getConsumptionTypeInstance(formValue['consumption_input_type']);

    productionTypeObject.setRequestData(request, formValue, { productionData: this.manager.productionData });
    consumptionTypeObject.setRequestData(request, formValue, {
      consumptionFileData: this.manager.consumptionFileData,
      tariffHasTwoPrices: this.manager.tariffHasTwoPrices,
    });
    if (request.productionsHourly) {
      request.systemCapacity = formValue['system_capacity'];
    }

    if (formValue['project_name']) {
      request.projectName = formValue['project_name'];
      const user = this.authService.getUserDataValue();
      request.investorName = `${ user?.firstName } ${ user?.lastName }`;
      request.address = formValue['address'];
    }

    const analysisCall: Observable<CalculatorResultWrapper> = this.analysisService.getAdvancedAnalysis(request, productionTypeObject.getProductionType(), consumptionTypeObject.getConsumptionType());

    this.loadingService.showLoader();
    analysisCall.subscribe(
      (resultWrapper) => {
        this.resultCalculated.emit(resultWrapper);
      },
    ).add(() => {
      this.loadingService.hideLoader();
    });
  }

  roofSegmentSelected(roofSegment: RoofSegmentSizeAndSunshineStats) {
    this.formGroup.controls['area'].setValue(roofSegment.stats.areaMeters2.toFixed());
    this.formGroup.controls['azimuth'].setValue(roofSegment.azimuthDegrees.toFixed());
    this.formGroup.controls['tilt'].setValue(roofSegment.pitchDegrees.toFixed());
    this.formGroup.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }
}

export enum ConsumptionValueInputType {
  MonthByMonth = 'MonthByMonth',
  MonthlyAverage = 'MonthlyAverage',
  Hourly = 'Hourly',
  MonthlyBill = 'MonthlyBill',
}


export enum SiteDataInputType {
  NREL = 'NREL',
  PVGIS = 'PVGIS',
  PVSYS = 'PVSYS',
  PVSOL = 'PVSOL',
}
